/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Pooja } from '../data/pooja-data';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

interface PoojaCardProps {
  pooja: Pooja;
  onSelect: (poojaId: number) => void;
  isSelected: boolean;
}

const GOLD = '#d4af37';

/** Renders pooja name with the bracketed English portion marked as notranslate */
function PoojaName({ name }: { name: string }) {
  const match = name.match(/^(.*?)(\(.*\))$/);
  if (!match) return <span className="notranslate" lang="ml">{name}</span>;
  return (
    <>
      <span className="notranslate" lang="ml">{match[1]}</span>
      <span className="notranslate" translate="no">{match[2]}</span>
    </>
  );
}

const DEITY_MAP: Record<string, { gradient: string; bg: string; label: string; icon: string }> = {
  BHAGAVATHY: { gradient: 'linear-gradient(135deg, #c0392b, #e74c3c)', bg: '#c0392b', label: 'Devi', icon: '\u0D26\u0D47' },
  SIVA:       { gradient: 'linear-gradient(135deg, #2c3e50, #34495e)', bg: '#2c3e50', label: 'Shiva', icon: '\u0D36' },
  VISHNU:     { gradient: 'linear-gradient(135deg, #1a5276, #2980b9)', bg: '#1a5276', label: 'Vishnu', icon: '\u0D35' },
  GANAPATHY:  { gradient: 'linear-gradient(135deg, #d35400, #e67e22)', bg: '#d35400', label: 'Ganapathy', icon: '\u0D17' },
  AYYAPPA:    { gradient: 'linear-gradient(135deg, #117a65, #1abc9c)', bg: '#117a65', label: 'Ayyappa', icon: '\u0D05' },
  GENERAL:    { gradient: 'linear-gradient(135deg, #7d6608, #b8960c)', bg: '#7d6608', label: 'General', icon: '\u0D13\u0D02' },
};

export default function PoojaCard({ pooja, onSelect, isSelected }: PoojaCardProps) {
  const [showOverlay, setShowOverlay] = React.useState(false);

  React.useEffect(() => {
    if (!isSelected) setShowOverlay(false);
  }, [isSelected]);

  const handleSelect = (e: React.MouseEvent) => {
    if (!isSelected) {
      onSelect(pooja.id);
      setShowOverlay(true);
    }
    e.stopPropagation();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(pooja.id);
    setShowOverlay(false);
  };

  const deity = DEITY_MAP[pooja.deity] || DEITY_MAP.GENERAL;

  return (
    <Box className="notranslate" translate="no" sx={{ position: 'relative', width: '100%', minHeight: { xs: 260, sm: 300 } }}>
      <Box
        onClick={handleSelect}
        sx={{
          position: 'relative',
          height: '100%',
          minHeight: { xs: 260, sm: 300 },
          borderRadius: '22px',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 450ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          bgcolor: '#fff',
          border: isSelected
            ? '2px solid ' + GOLD
            : '1px solid rgba(0,0,0,0.06)',
          boxShadow: isSelected
            ? '0 12px 40px rgba(212,175,55,0.22), 0 0 0 4px rgba(212,175,55,0.06)'
            : '0 2px 8px rgba(0,0,0,0.04)',
          '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.12)',
            '& .hoverOverlay': { opacity: 1, transform: 'translateY(0)' },
            '& .deityOrb': { transform: 'scale(1.15) rotate(8deg)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' },
            '& .goldAccent': { width: '60%' },
            '& .priceBlock': { transform: 'translateY(-2px)' },
          },
        }}
      >
        {/* ── Deity header band ── */}
        <Box sx={{
          position: 'relative',
          height: { xs: 70, sm: 80 },
          background: deity.gradient,
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {/* Pattern dots */}
          <Box sx={{
            position: 'absolute', inset: 0, opacity: 0.08,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '12px 12px',
          }} />

          {/* Deity label in band */}
          <Box sx={{ position: 'absolute', top: 12, left: 14 }}>
            <Typography sx={{
              fontSize: '0.58rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)',
              letterSpacing: 2, textTransform: 'uppercase',
            }}>
              {deity.label}
            </Typography>
          </Box>

          {/* Selected badge top-right */}
          {isSelected && !showOverlay && (
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '9px',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
            </Box>
          )}

          {/* Bottom curve */}
          <Box sx={{
            position: 'absolute', bottom: -1, left: 0, right: 0, height: 24,
            bgcolor: '#fff',
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
          }} />
        </Box>

        {/* ── Deity Orb (overlapping) ── */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: -4.5, sm: -5 }, mb: 1.5, position: 'relative', zIndex: 3 }}>
          <Box className="deityOrb" sx={{
            width: { xs: 52, sm: 58 }, height: { xs: 52, sm: 58 },
            borderRadius: '50%',
            background: deity.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            border: '3px solid #fff',
            transition: 'all 500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}>
            <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, color: '#fff', fontWeight: 700, lineHeight: 1 }}>
              {deity.icon}
            </Typography>
          </Box>
        </Box>

        {/* ── Name ── */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, textAlign: 'center', mb: 0.5 }}>
          <Typography sx={{
            fontSize: { xs: '0.82rem', sm: '0.9rem' },
            fontWeight: 700,
            color: '#1a1a1a',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            <PoojaName name={pooja.name} />
          </Typography>
        </Box>

        {/* Gold accent line - grows on hover */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <Box className="goldAccent" sx={{
            width: '30%', height: 2,
            background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
            borderRadius: 1,
            transition: 'width 600ms ease',
          }} />
        </Box>

        {pooja.description && (
          <Box sx={{ px: { xs: 1.5, sm: 2 }, textAlign: 'center', mb: 1 }}>
            <Typography sx={{
              fontSize: '0.68rem',
              color: '#b5b5b5',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {pooja.description}
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {/* ── Price Footer ── */}
        <Box sx={{
          mx: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          px: 2, py: 1.2,
          borderRadius: '14px',
          bgcolor: 'rgba(212,175,55,0.04)',
          border: '1px solid rgba(212,175,55,0.08)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          transition: 'all 400ms ease',
        }}>
          <Box className="priceBlock" sx={{ transition: 'transform 400ms ease' }}>
            <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#ccc', letterSpacing: 1, textTransform: 'uppercase', lineHeight: 1, mb: 0.3 }}>
              Dakshina
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 900, color: GOLD, lineHeight: 1, letterSpacing: '-0.02em' }}>
              {'\u20B9'}{pooja.price.toLocaleString()}
            </Typography>
          </Box>
          <LocalFireDepartmentIcon sx={{ fontSize: 18, color: GOLD, opacity: 0.25 }} />
        </Box>

        {/* ── HOVER OVERLAY ── */}
        <Box className="hoverOverlay" sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.4) 75%, transparent 100%)',
          backdropFilter: 'blur(4px)',
          opacity: 0,
          transform: 'translateY(16px)',
          transition: 'all 450ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          p: { xs: 2, sm: 2.5 }, zIndex: 5,
          borderRadius: '22px',
          pointerEvents: isSelected ? 'none' : 'auto',
        }}>
          {/* Overlay price */}
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: GOLD, letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.5 }}>
            {deity.label} Pooja
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, fontWeight: 800, color: '#fff', mb: 0.8, lineHeight: 1.25,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            <PoojaName name={pooja.name} />
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.92rem', md: '1.1rem' }, fontWeight: 900, color: GOLD, mb: 2, lineHeight: 1 }}>
            {'\u20B9'}{pooja.price.toLocaleString()}
          </Typography>

          {/* CTA button */}
          <Box
            onClick={handleSelect}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
              py: 1.2, borderRadius: '14px',
              bgcolor: GOLD,
              boxShadow: '0 6px 24px rgba(212,175,55,0.4)',
              transition: 'all 200ms ease',
              '&:hover': { bgcolor: '#e5c158', transform: 'scale(1.02)' },
            }}
          >
            <AddShoppingCartIcon sx={{ fontSize: 18, color: '#fff' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>
              Add to Booking
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── SELECTION OVERLAY ── */}
      {showOverlay && (
        <Box className="notranslate" translate="no" sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(165deg, rgba(255,253,245,0.97) 0%, rgba(255,249,230,0.97) 100%)',
          backdropFilter: 'blur(24px)',
          zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '22px',
          border: '2px solid ' + GOLD,
          boxShadow: '0 0 0 5px rgba(212,175,55,0.06), 0 12px 40px rgba(212,175,55,0.15)',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, textAlign: 'center' }}>

            {/* Success ring */}
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%',
              border: '3px solid ' + GOLD,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mb: 2,
              animation: 'ringPop 500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              '@keyframes ringPop': {
                '0%': { transform: 'scale(0)', opacity: 0 },
                '50%': { transform: 'scale(1.2)' },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: GOLD }} />
            </Box>

            <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#1a1a1a', mb: 0.3 }}>
              Added!
            </Typography>
            <Typography sx={{
              fontSize: '0.68rem', color: '#bbb', mb: 0.5,
              maxWidth: 160,
              display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              <PoojaName name={pooja.name} />
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, fontWeight: 900, color: GOLD, mb: 2.5 }}>
              {'\u20B9'}{pooja.price.toLocaleString()}
            </Typography>

            <Button
              size="small"
              onClick={handleRemove}
              sx={{
                color: '#bbb', fontSize: '0.75rem', fontWeight: 600,
                textTransform: 'none', px: 3.5, py: 0.6,
                borderRadius: '12px',
                border: '1.5px solid rgba(0,0,0,0.06)',
                transition: 'all 250ms ease',
                '&:hover': {
                  borderColor: '#e74c3c',
                  color: '#e74c3c',
                  bgcolor: 'rgba(231,76,60,0.04)',
                  boxShadow: '0 4px 12px rgba(231,76,60,0.1)',
                },
              }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
